// Review hooks — aggregated data for /review dashboard + legacy content-piece actions
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { ContentPiece } from "../../../shared/types";

// ── Legacy content-piece hooks (used by /review/:runId and tests) ──

export function useApprovePiece() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pieceId: string) =>
      api.post<{ data: ContentPiece }>(`/api/content/pieces/${pieceId}/approve`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["content-runs"] });
      // FR-13: keep the workspace approval KPI + content summary fresh
      // immediately after approve/reject/regenerate (they have staleTime, so without
      // this the KPI stays overstated for up to 60s).
      qc.invalidateQueries({ queryKey: ["brand-stats"] });
      qc.invalidateQueries({ queryKey: ["content-summary"] });
    },
  });
}

export function useRejectPiece() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pieceId: string) =>
      api.post<{ data: ContentPiece }>(`/api/content/pieces/${pieceId}/reject`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["content-runs"] });
      // FR-13: keep the workspace approval KPI + content summary fresh
      // immediately after approve/reject/regenerate (they have staleTime, so without
      // this the KPI stays overstated for up to 60s).
      qc.invalidateQueries({ queryKey: ["brand-stats"] });
      qc.invalidateQueries({ queryKey: ["content-summary"] });
    },
  });
}

export function useRegeneratePiece(runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { platform: string }) =>
      api.post<{ data: ContentPiece }>(`/api/content/runs/${runId}/regenerate`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["content-runs"] });
      // FR-13: keep the workspace approval KPI + content summary fresh
      // immediately after approve/reject/regenerate (they have staleTime, so without
      // this the KPI stays overstated for up to 60s).
      qc.invalidateQueries({ queryKey: ["brand-stats"] });
      qc.invalidateQueries({ queryKey: ["content-summary"] });
    },
  });
}

// ── New review-dashboard + cadence hooks ──

export {
  useBrandReview as useReviewDashboard,
} from "./useBrandReview";

export {
  useCadence,
  useCreateAdhocCadenceSlot,
  useRunBrandTask,
} from "./useCadence";
